import { useState } from "react";
import { LuMail, LuMessageSquare, LuChevronDown, LuChevronUp, LuCheck, LuCopy, LuSend } from "react-icons/lu";
import { FiHelpCircle } from "react-icons/fi";
import { useToast } from "../hooks/useToast";

interface FAQItem {
    question: string;
    answer: string;
}

const FAQS: FAQItem[] = [
    {
        question: "How do dynamic credits work?",
        answer: "We use a pay-as-you-extract credit model. Email Discovery costs 0.2 credits per unique email found. Profile Details cost 1.0 credit per profile lookup. Extracting follower/following network lists costs 0.3 credits per fetch request. If your credits run out, the job aborts immediately and saves all accumulated results so you can download them as a CSV."
    },
    {
        question: "Where can I find and download my scrape results?",
        answer: "All your active and completed jobs are saved under the Scrape History tab. From there, you can view real-time progress, visualize results, and download the full dataset as a clean, standardized CSV file."
    },
    {
        question: "Can I navigate away or close the browser during a scrape?",
        answer: "Yes, absolutely! All scrape jobs run asynchronously on our high-performance background worker queues. You can safely close the application or turn off your device. The worker will continue the extraction, and the results will be waiting for you in your history."
    },
    {
        question: "Why do some profiles not yield emails?",
        answer: "We only extract publicly available emails that users have chosen to display in their bios or contact buttons. If a profile doesn't have an email listed, it won't be charged the 0.2 credits Email Discovery fee."
    },
    {
        question: "How do target locations and proxies work?",
        answer: "To ensure high delivery rates and localized results, you can select a target proxy group (e.g. India, Other) during setup. We dynamically route requests through optimized account pools matching that location."
    }
];

export default function Support() {
    const { showToast } = useToast();
    const [expandedFaq, setExpandedFaq] = useState<number | null>(0);
    const [copied, setCopied] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [formData, setFormData] = useState({ name: "", email: "", subject: "", message: "" });

    const handleCopyEmail = () => {
        navigator.clipboard.writeText("support@igleads.co");
        setCopied(true);
        showToast({ title: "Email Copied", message: "support@igleads.co copied to clipboard", variant: "success", duration: 3000 });
        setTimeout(() => setCopied(false), 2000);
    };

    const handleFormSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!formData.name || !formData.email || !formData.subject || !formData.message) {
            showToast({ title: "Validation Error", message: "Please fill in all fields", variant: "error" });
            return;
        }

        setIsSubmitting(true);
        // Mock API submission
        await new Promise(resolve => setTimeout(resolve, 1500));
        setIsSubmitting(false);

        showToast({
            title: "Message Sent",
            message: "Thank you! Our support team will get back to you shortly.",
            variant: "success"
        });
        setFormData({ name: "", email: "", subject: "", message: "" });
    };

    const toggleFaq = (index: number) => {
        setExpandedFaq(expandedFaq === index ? null : index);
    };

    return (
        <div className="p-6 md:p-10 bg-gradient-to-b from-purple-50/50 via-slate-50 to-white min-h-screen">
            <div className="max-w-6xl mx-auto">
                {/* Header */}
                <div className="mb-12 text-center relative">
                    <span className="inline-block px-3 py-1 rounded-full bg-violet-100/80 text-violet-700 font-bold text-[10px] uppercase tracking-widest mb-3.5 border border-violet-200/50 shadow-xs">
                        Help Center & Support
                    </span>
                    <h1 className="text-3xl md:text-5xl font-black text-slate-900 tracking-tight leading-tight">
                        How can we{" "}
                        <span className="bg-gradient-to-r from-violet-600 to-fuchsia-600 bg-clip-text text-transparent">
                            help you today?
                        </span>
                    </h1>
                    <p className="text-slate-500 text-sm md:text-base mt-3 max-w-xl mx-auto font-medium">
                        Find answers to common questions, learn how credits and proxy groups operate, or get in touch with our engineering support team directly.
                    </p>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    {/* Left: FAQs */}
                    <div className="lg:col-span-7 space-y-6">
                        <div className="bg-white/80 backdrop-blur-md rounded-3xl p-6 md:p-8 border border-slate-200/60 shadow-xl shadow-slate-100/50">
                            <h2 className="text-lg font-bold text-slate-800 mb-6 flex items-center gap-2.5">
                                <FiHelpCircle className="text-violet-600" size={22} />
                                Frequently Asked Questions
                            </h2>

                            <div className="space-y-3.5">
                                {FAQS.map((faq, index) => {
                                    const isExpanded = expandedFaq === index;
                                    return (
                                        <div 
                                            key={index} 
                                            className={`transition-all duration-300 rounded-2xl border ${
                                                isExpanded 
                                                    ? "bg-violet-50/20 border-violet-150 shadow-xs" 
                                                    : "bg-white/40 border-slate-100 hover:border-violet-200 hover:bg-white/60"
                                            }`}
                                        >
                                            <button
                                                onClick={() => toggleFaq(index)}
                                                className="w-full flex items-center justify-between p-4 text-left font-bold text-slate-700 hover:text-violet-600 transition-colors focus:outline-none cursor-pointer"
                                            >
                                                <span className="text-sm md:text-base leading-snug">{faq.question}</span>
                                                <div className={`w-7 h-7 rounded-lg flex items-center justify-center transition-all ${
                                                    isExpanded ? "bg-violet-100 text-violet-600" : "bg-slate-50 text-slate-400"
                                                }`}>
                                                    {isExpanded ? (
                                                        <LuChevronUp size={16} />
                                                    ) : (
                                                        <LuChevronDown size={16} />
                                                    )}
                                                </div>
                                            </button>
                                            <div
                                                className={`transition-all duration-300 overflow-hidden ${
                                                    isExpanded ? "max-h-[250px] opacity-100" : "max-h-0 opacity-0"
                                                }`}
                                            >
                                                <p className="text-sm text-slate-500 leading-relaxed p-4 pt-0 border-t border-slate-100/50 font-medium">
                                                    {faq.answer}
                                                </p>
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        </div>

                        {/* Support Card info */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-slate-200/60 shadow-sm flex flex-col justify-between group hover:border-violet-300 hover:-translate-y-0.5 transition-all duration-300">
                                <div>
                                    <div className="w-10 h-10 rounded-xl bg-violet-50 flex items-center justify-center mb-4 text-violet-600 shadow-xs">
                                        <LuMail size={20} />
                                    </div>
                                    <h3 className="font-bold text-slate-800 text-sm mb-1">Email Support</h3>
                                    <p className="text-xs text-slate-400 font-medium leading-relaxed mb-4">
                                        Send us a detailed message. We typically respond within 12 hours.
                                    </p>
                                </div>
                                <button
                                    onClick={handleCopyEmail}
                                    className="w-full py-2.5 rounded-xl border border-slate-200 hover:border-violet-500 text-xs font-bold text-slate-600 hover:text-violet-600 transition-all flex items-center justify-center gap-1.5 bg-slate-50/50 hover:bg-violet-50/30 cursor-pointer"
                                >
                                    {copied ? (
                                        <><LuCheck className="text-emerald-500" size={14} /> Copied</>
                                    ) : (
                                        <><LuCopy size={13} /> support@igleads.co</>
                                    )}
                                </button>
                            </div>

                            <div className="bg-white/80 backdrop-blur-md rounded-2xl p-6 border border-slate-200/60 shadow-sm flex flex-col justify-between group hover:border-fuchsia-300 hover:-translate-y-0.5 transition-all duration-300">
                                <div>
                                    <div className="w-10 h-10 rounded-xl bg-fuchsia-50 flex items-center justify-center mb-4 text-fuchsia-600 shadow-xs">
                                        <LuMessageSquare size={20} />
                                    </div>
                                    <h3 className="font-bold text-slate-800 text-sm mb-1">Live Help</h3>
                                    <p className="text-xs text-slate-400 font-medium leading-relaxed mb-4">
                                        Need dynamic troubleshooting? Drop your ticket message on the right.
                                    </p>
                                </div>
                                <a
                                    href="mailto:support@igleads.co"
                                    className="w-full py-2.5 rounded-xl border border-slate-200 hover:border-fuchsia-500 text-xs font-bold text-slate-600 hover:text-fuchsia-600 transition-all flex items-center justify-center gap-1.5 bg-slate-50/50 hover:bg-fuchsia-50/30 cursor-pointer"
                                >
                                    Open Support Mail
                                </a>
                            </div>
                        </div>
                    </div>

                    {/* Right: Contact Form */}
                    <div className="lg:col-span-5">
                        <div className="bg-white/85 backdrop-blur-md rounded-3xl p-6 md:p-8 border border-slate-200/60 shadow-xl shadow-slate-100/50">
                            <h2 className="text-lg font-bold text-slate-800 mb-2 flex items-center gap-2">
                                <LuSend className="text-violet-600" size={20} />
                                Send Message
                            </h2>
                            <p className="text-xs text-slate-450 font-medium mb-6">
                                Submit a ticket and our development team will review it.
                            </p>

                            <form onSubmit={handleFormSubmit} className="space-y-4">
                                <div>
                                    <label className="text-[10px] font-extrabold text-slate-500 mb-1.5 block uppercase tracking-wider">
                                        Your Name
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.name}
                                        onChange={e => setFormData({ ...formData, name: e.target.value })}
                                        placeholder="e.g. John Doe"
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 focus:bg-white outline-none bg-slate-50/50 text-sm font-medium transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="text-[10px] font-extrabold text-slate-500 mb-1.5 block uppercase tracking-wider">
                                        Email Address
                                    </label>
                                    <input
                                        type="email"
                                        value={formData.email}
                                        onChange={e => setFormData({ ...formData, email: e.target.value })}
                                        placeholder="e.g. john@example.com"
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 focus:bg-white outline-none bg-slate-50/50 text-sm font-medium transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="text-[10px] font-extrabold text-slate-500 mb-1.5 block uppercase tracking-wider">
                                        Subject
                                    </label>
                                    <input
                                        type="text"
                                        value={formData.subject}
                                        onChange={e => setFormData({ ...formData, subject: e.target.value })}
                                        placeholder="e.g. Credit Top-up Issue"
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 focus:bg-white outline-none bg-slate-50/50 text-sm font-medium transition-all"
                                    />
                                </div>

                                <div>
                                    <label className="text-[10px] font-extrabold text-slate-500 mb-1.5 block uppercase tracking-wider">
                                        Message Details
                                    </label>
                                    <textarea
                                        rows={4}
                                        value={formData.message}
                                        onChange={e => setFormData({ ...formData, message: e.target.value })}
                                        placeholder="Please describe your issue in detail..."
                                        className="w-full px-4 py-3 rounded-xl border border-slate-200 focus:border-violet-500 focus:ring-4 focus:ring-violet-500/10 focus:bg-white outline-none bg-slate-50/50 text-sm font-medium transition-all resize-none"
                                    />
                                </div>

                                <button
                                    type="submit"
                                    disabled={isSubmitting}
                                    className={`w-full py-3.5 rounded-xl font-bold text-sm transition-all flex items-center justify-center gap-2 mt-2 cursor-pointer
                                        ${isSubmitting
                                            ? "bg-slate-100 text-slate-400 cursor-not-allowed"
                                            : "bg-gradient-to-r from-violet-600 to-fuchsia-600 text-white shadow-md shadow-violet-200 hover:shadow-lg hover:shadow-violet-200 hover:-translate-y-0.5 active:translate-y-0"
                                        }`}
                                >
                                    {isSubmitting ? (
                                        <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-650 rounded-full animate-spin" />
                                    ) : (
                                        <><LuSend size={15} /> Send Ticket</>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
